import { z } from 'zod';
import { WS_ROLES } from '../auth/claims.js';
import { SlugSchema, TimezoneSchema, WorkspaceNameSchema } from './workspace.js';

/**
 * Request and response bodies of the worker's member API (apps/worker/src/http/routes/members.ts).
 * The browser validates its forms with the same schemas and parses the responses with them.
 */

export const EmailSchema = z
  .string()
  .trim()
  .toLowerCase()
  .pipe(z.email({ error: 'Enter a valid email address' }).max(254, 'That email is too long'));

export const FullNameSchema = z
  .string()
  .trim()
  .min(1, 'Enter a name')
  .max(120, 'Use at most 120 characters');

/** bcrypt (Supabase Auth) ignores bytes after 72. Matches minimum_password_length in Supabase. */
export const PasswordSchema = z
  .string()
  .min(10, 'Use at least 10 characters')
  .max(72, 'Use at most 72 characters');

export const InviteRequestSchema = z.object({
  email: EmailSchema,
  fullName: FullNameSchema,
  role: z.enum(WS_ROLES),
});
export type InviteRequest = z.infer<typeof InviteRequestSchema>;

/**
 * `link`: the admin receives a single-use sign-in link to pass on (no email is sent).
 * `email`: Supabase Auth emails the invite (needs working SMTP, e.g. Resend).
 */
export const INVITE_DELIVERIES = ['link', 'email'] as const;
export type InviteDelivery = (typeof INVITE_DELIVERIES)[number];

export const InviteResponseSchema = z.object({
  membershipId: z.uuid(),
  userId: z.uuid(),
  delivery: z.enum(INVITE_DELIVERIES),
  /** Present when delivery is `link`. Single-use; expires with Supabase's email OTP expiry. */
  inviteLink: z.url().optional(),
});
export type InviteResponse = z.infer<typeof InviteResponseSchema>;

export const InvitationSchema = z.object({
  workspaceId: z.uuid(),
  workspaceName: z.string(),
  role: z.enum(WS_ROLES),
  status: z.enum(['invited', 'active']),
  invitedByName: z.string().nullable(),
  fullName: z.string(),
});
export type Invitation = z.infer<typeof InvitationSchema>;

export const AcceptInvitationRequestSchema = z.object({ fullName: FullNameSchema });
export type AcceptInvitationRequest = z.infer<typeof AcceptInvitationRequestSchema>;

export const CreateWorkspaceRequestSchema = z.object({
  name: WorkspaceNameSchema,
  slug: SlugSchema,
  timezone: TimezoneSchema,
  adminEmail: EmailSchema,
  adminFullName: FullNameSchema,
});
export type CreateWorkspaceRequest = z.infer<typeof CreateWorkspaceRequestSchema>;

export const CreateWorkspaceResponseSchema = z.object({
  workspaceId: z.uuid(),
  invite: InviteResponseSchema,
});
export type CreateWorkspaceResponse = z.infer<typeof CreateWorkspaceResponseSchema>;

/** Error body of every worker API error. `code` is stable; `message` is for people. */
export const ApiErrorSchema = z.object({ error: z.string(), message: z.string() });
export type ApiError = z.infer<typeof ApiErrorSchema>;
